import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Reply, Edit3, Trash2, Paperclip, ChevronDown, ChevronRight } from "lucide-react";
import { MentionTextarea } from "@/components/mention-textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { invalidateIndicatorCaches } from "@/lib/cache-invalidation";
import { STATUS_COLORS } from "@/lib/canvas-utils";
import type { PageIndicator, IndicatorComment, DocumentUser, CommentAttachment } from "@shared/schema";
import { Linkify } from "@/components/linkify";

const STATUS_OPTIONS = [
  { value: "open", label: "Open", color: "bg-amber-500" },
  { value: "inProgress", label: "In Progress", color: "bg-sky-500" },
  { value: "resolved", label: "Resolved", color: "bg-emerald-500" },
  { value: "skipped", label: "Skipped", color: "bg-gray-500" },
] as const;

interface PageCommentsSectionProps {
  pageId: string;
  versionId?: string;
  documentId: string;
  versionNumber: number;
  currentUser: { id: string; name: string; email: string } | null;
  documentColor: string;
  documentUsers: DocumentUser[];
  onIndicatorClick?: (indicatorNumber: number, pageNumber?: number) => void;
  showAllPages?: boolean;
  selectedStatuses?: Set<string>;
  updateIndicatorStatusMutation?: {
    mutate: (params: { indicatorId: string; status: string }) => void;
    isPending?: boolean;
  };
}

type IndicatorWithComments = PageIndicator & {
  comments: (IndicatorComment & { 
    user: DocumentUser; 
    attachments: CommentAttachment[] 
  })[];
  pageNumber?: number;
};

export function PageCommentsSection({ 
  pageId,
  versionId,
  documentId,
  versionNumber,
  currentUser, 
  documentColor, 
  documentUsers,
  onIndicatorClick,
  showAllPages = false,
  selectedStatuses = new Set(['open', 'inProgress', 'resolved', 'skipped']),
  updateIndicatorStatusMutation
}: PageCommentsSectionProps) {
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [newReply, setNewReply] = useState("");
  const [mentionedDocUserIds, setMentionedDocUserIds] = useState<Set<string>>(new Set());
  const [editText, setEditText] = useState("");
  const [completedSectionOpen, setCompletedSectionOpen] = useState(false);
  const [completedSectionsByPage, setCompletedSectionsByPage] = useState<Record<number, boolean>>({});
  const { toast } = useToast();

  const togglePageCompletedSection = (pageNum: number) => {
    setCompletedSectionsByPage(prev => ({
      ...prev,
      [pageNum]: !prev[pageNum]
    }));
  };

  // Fetch indicators with comments - either for single page or all pages in version
  const { data: indicatorsWithComments = [], isLoading, refetch } = useQuery<IndicatorWithComments[]>({
    queryKey: showAllPages 
      ? ["/api/versions", versionId, "indicators-with-comments"]
      : ["/api/pages", pageId, "indicators-with-comments"],
    enabled: showAllPages ? !!versionId && !!pageId : !!pageId,
    refetchOnWindowFocus: false,
  });

  // Note: Refetch happens automatically when queryKey changes

  const handleReply = async (indicatorId: string) => {
    if (!newReply.trim() || !currentUser) return;
    
    try {
      await apiRequest("POST", `/api/indicators/${indicatorId}/comments`, {
        content: newReply.trim(),
        userId: currentUser.id,
        mentionedUserIds: Array.from(mentionedDocUserIds),
      });
      
      setNewReply("");
      setMentionedDocUserIds(new Set());
      setReplyingTo(null);
      
      // Invalidate both indicator and comment caches for full sync
      invalidateIndicatorCaches(pageId, documentId, versionNumber, versionId);
      
      toast({
        title: "Reply added",
        description: "Your reply has been posted successfully.",
      });
    } catch (error) {
      console.error("Failed to add reply:", error);
      toast({
        title: "Error",
        description: "Failed to add reply. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return;
    
    try {
      await apiRequest("PATCH", `/api/comments/${commentId}`, {
        content: editText.trim()
      });
      
      setEditingComment(null);
      setEditText("");
      
      // Invalidate both indicator and comment caches for full sync
      invalidateIndicatorCaches(pageId, documentId, versionNumber, versionId);
      
      toast({
        title: "Comment updated",
        description: "Your comment has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to edit comment:", error);
      toast({
        title: "Error",
        description: "Failed to update comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentId) return;
    
    try {
      await apiRequest("DELETE", `/api/comments/${deleteCommentId}`);
      
      // Invalidate both indicator and comment caches for full sync
      invalidateIndicatorCaches(pageId, documentId, versionNumber, versionId);
      
      setDeleteCommentId(null);
      
      toast({
        title: "Comment deleted",
        description: "The comment has been deleted successfully.",
      });
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast({
        title: "Error", 
        description: "Failed to delete comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleIndicatorClick = (indicatorNumber: number, pageNumber?: number) => {
    if (onIndicatorClick) {
      onIndicatorClick(indicatorNumber, pageNumber);
    }
  };

  // Helper function to get status badge styling
  const getStatusBadgeStyles = (status: string) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS];
    if (!color) return null;
    
    return {
      backgroundColor: color + '20', // 20% opacity for background
      color: color,
      border: `1px solid ${color}40`, // 40% opacity for border
    };
  };

  // Helper function to get proper status labels
  const getStatusLabel = (status: string): string => {
    const statusLabels = {
      open: 'Open',
      inProgress: 'In Progress',
      resolved: 'Resolved',
      skipped: 'Skipped'
    };
    return statusLabels[status as keyof typeof statusLabels] || status;
  };

  // Helper function to render indicator section
  const renderIndicatorSection = (indicators: IndicatorWithComments[]) => (
    <div className="space-y-6">
      {indicators.map((indicator) => (
        <div key={indicator.id} className="border-l-2 pl-4" style={{ borderLeftColor: documentColor }}>
          {/* Indicator Header */}
          <div 
            className="flex items-center gap-2 mb-3 cursor-pointer hover:bg-muted/50 rounded p-1 -ml-1"
            onClick={() => handleIndicatorClick(indicator.number, indicator.pageNumber)}
            data-testid={`indicator-header-${indicator.number}`}
          >
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: documentColor }}
            >
              {indicator.number}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <span className="whitespace-nowrap">
                {indicator.comments.length} comment{indicator.comments.length !== 1 ? 's' : ''}
              </span>
              {updateIndicatorStatusMutation ? (
                <Select
                  value={indicator.status}
                  onValueChange={(status) => {
                    updateIndicatorStatusMutation.mutate({
                      indicatorId: indicator.id,
                      status
                    });
                  }}
                  disabled={updateIndicatorStatusMutation.isPending}
                >
                  <SelectTrigger
                    className="h-auto w-auto px-2 py-0.5 rounded-full text-xs font-medium border-0 focus:ring-0 gap-1"
                    style={{
                      backgroundColor: STATUS_COLORS[indicator.status as keyof typeof STATUS_COLORS] + '20',
                      color: STATUS_COLORS[indicator.status as keyof typeof STATUS_COLORS],
                      border: `1px solid ${STATUS_COLORS[indicator.status as keyof typeof STATUS_COLORS]}40`,
                    }}
                    data-testid={`status-select-${indicator.status}-${indicator.number}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>{getStatusLabel(indicator.status)}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${option.color}`}></div>
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : getStatusBadgeStyles(indicator.status) && (
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={getStatusBadgeStyles(indicator.status)!}
                  data-testid={`status-badge-${indicator.status}-${indicator.number}`}
                >
                  {getStatusLabel(indicator.status)}
                </span>
              )}
            </div>
          </div>

          {/* Comments Thread */}
          <div className="space-y-3">
            {indicator.comments.map((comment, commentIndex) => {
              const isEditing = editingComment === comment.id;
              const isOwner = currentUser && comment.userId === currentUser.id;
              
              return (
                <div key={comment.id} className="group">
                  {/* Comment Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs">
                          {comment.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-medium">{comment.user?.name || 'Anonymous'}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    
                    {isOwner && (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            setEditingComment(comment.id);
                            setEditText(comment.content);
                          }}
                          data-testid={`edit-comment-${comment.id}`}
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDeleteCommentId(comment.id)}
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Comment Content */}
                  {isEditing ? (
                    <div className="space-y-2">
                      <MentionTextarea
                        value={editText}
                        onChange={setEditText}
                        users={documentUsers}
                        placeholder="Edit your comment..."
                        className="text-sm bg-background text-foreground border border-border rounded p-2 w-full min-h-[60px] focus:ring-2 focus:ring-ring focus:outline-none"
                        minRows={2}
                        maxRows={4}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleEditComment(comment.id)}
                          disabled={!editText.trim()}
                          style={{ backgroundColor: documentColor }}
                          className="text-white hover:opacity-90"
                          data-testid={`save-edit-${comment.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingComment(null);
                            setEditText("");
                          }}
                          data-testid={`cancel-edit-${comment.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-foreground mb-2 whitespace-pre-wrap break-words">
                        <Linkify>{comment.content}</Linkify>
                      </div>
                      
                      {/* Attachments */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {comment.attachments.map((attachment) => (
                            <div key={attachment.id} className="flex items-center gap-1 text-xs bg-muted rounded px-2 py-1">
                              <Paperclip className="w-3 h-3" />
                              <a 
                                href={attachment.storageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline truncate max-w-32"
                                data-testid={`attachment-link-${attachment.id}`}
                              >
                                {attachment.originalFilename}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Reply Button */}
                  {!isEditing && currentUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs px-2 mt-1 opacity-60 hover:opacity-100"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                      data-testid={`reply-button-${comment.id}`}
                    >
                      <Reply className="w-3 h-3 mr-1" />
                      Reply
                    </Button>
                  )}

                  {/* Reply Form */}
                  {replyingTo === comment.id && currentUser && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      <MentionTextarea
                        value={newReply}
                        onChange={setNewReply}
                        onMentionSelect={(user) => setMentionedDocUserIds(prev => new Set(prev).add(user.id))}
                        users={documentUsers}
                        placeholder="Write a reply..."
                        className="text-sm bg-background text-foreground border border-border rounded p-2 w-full focus:ring-2 focus:ring-ring focus:outline-none"
                        minRows={2}
                        maxRows={4}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReply(indicator.id)}
                          disabled={!newReply.trim()}
                          style={{ backgroundColor: documentColor }}
                          className="text-white hover:opacity-90"
                          data-testid={`submit-reply-${comment.id}`}
                        >
                          Reply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReplyingTo(null);
                            setNewReply("");
                            setMentionedDocUserIds(new Set());
                          }}
                          data-testid={`cancel-reply-${comment.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/3"></div>
          <div className="space-y-2">
            <div className="h-3 bg-muted rounded w-full"></div>
            <div className="h-3 bg-muted rounded w-2/3"></div>
          </div>
        </div>
      </Card>
    );
  }

  // Filter indicators that have comments and match selected statuses
  const indicatorsWithCommentsFiltered = indicatorsWithComments.filter(
    indicator => indicator.comments && indicator.comments.length > 0 && selectedStatuses.has(indicator.status)
  );

  // Separate indicators into active and completed groups
  const activeIndicators = indicatorsWithCommentsFiltered.filter(
    indicator => indicator.status === 'open' || indicator.status === 'inProgress'
  );
  const completedIndicators = indicatorsWithCommentsFiltered.filter(
    indicator => indicator.status === 'resolved' || indicator.status === 'skipped'
  );

  if (indicatorsWithCommentsFiltered.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center text-muted-foreground">
          <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{showAllPages ? 'No comments in this version yet.' : 'No comments on this page yet.'}</p>
          <p className="text-xs mt-1">Click on the canvas to add a comment.</p>
        </div>
      </Card>
    );
  }

  if (showAllPages) {
    const indicatorsByPage = indicatorsWithCommentsFiltered.reduce((acc, indicator) => {
      const pageNum = indicator.pageNumber || 1;
      if (!acc[pageNum]) {
        acc[pageNum] = [];
      }
      acc[pageNum].push(indicator);
      return acc;
    }, {} as Record<number, IndicatorWithComments[]>);

    const sortedPageNumbers = Object.keys(indicatorsByPage).map(Number).sort((a, b) => a - b);

    return (
      <>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4" style={{ color: documentColor }} />
            <h3 className="font-medium text-sm">
              All Comments ({indicatorsWithCommentsFiltered.length})
            </h3>
          </div>

          <div className="space-y-8" data-testid="page-comments-list-all">
            {sortedPageNumbers.map((pageNum) => {
              const pageIndicators = indicatorsByPage[pageNum];
              const activePageIndicators = pageIndicators.filter(
                indicator => indicator.status === 'open' || indicator.status === 'inProgress'
              );
              const completedPageIndicators = pageIndicators.filter(
                indicator => indicator.status === 'resolved' || indicator.status === 'skipped'
              );

              return (
                <div key={pageNum} className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <h4 className="font-semibold text-sm" style={{ color: documentColor }}>
                      Page {pageNum}
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      ({pageIndicators.length} comment{pageIndicators.length !== 1 ? 's' : ''})
                    </span>
                  </div>

                  {activePageIndicators.length > 0 && (
                    <div>
                      {renderIndicatorSection(activePageIndicators)}
                    </div>
                  )}

                  {completedPageIndicators.length > 0 && (
                    <Collapsible 
                      open={completedSectionsByPage[pageNum] || false}
                      onOpenChange={() => togglePageCompletedSection(pageNum)}
                    >
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full flex items-center justify-between p-2 h-auto font-normal text-muted-foreground hover:bg-muted/50"
                          data-testid={`completed-comments-toggle-page-${pageNum}`}
                        >
                          <div className="flex items-center gap-2">
                            {completedSectionsByPage[pageNum] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            <span className="text-sm">
                              Completed ({completedPageIndicators.length})
                            </span>
                          </div>
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-hidden">
                        <div className="pt-2">
                          {renderIndicatorSection(completedPageIndicators)}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
          <AlertDialogContent className="max-w-[90vw] sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-base">Delete comment?</AlertDialogTitle>
              <AlertDialogDescription className="text-sm">
                This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogCancel 
                className="m-0 w-full sm:w-auto"
                data-testid="cancel-delete-comment"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteComment}
                className="m-0 w-full sm:w-auto text-white hover:opacity-90"
                style={{ backgroundColor: documentColor }}
                data-testid="confirm-delete-comment"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <>
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-4 h-4" style={{ color: documentColor }} />
          <h3 className="font-medium text-sm">
            Page Comments ({indicatorsWithCommentsFiltered.length})
          </h3>
        </div>

        <div className="space-y-6" data-testid="page-comments-list">
          {activeIndicators.length > 0 && (
            <div>
              {renderIndicatorSection(activeIndicators)}
            </div>
          )}

          {completedIndicators.length > 0 && (
            <Collapsible open={completedSectionOpen} onOpenChange={setCompletedSectionOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full flex items-center justify-between p-2 h-auto font-normal text-muted-foreground hover:bg-muted/50"
                  data-testid="completed-comments-toggle"
                >
                  <div className="flex items-center gap-2">
                    {completedSectionOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    <span className="text-sm">
                      Completed ({completedIndicators.length})
                    </span>
                  </div>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 overflow-hidden">
                <div className="pt-2">
                  {renderIndicatorSection(completedIndicators)}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {activeIndicators.length === 0 && completedIndicators.length === 0 && (
            <div className="text-center text-muted-foreground py-6">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No comments on this page yet.</p>
              <p className="text-xs mt-1">Click on the canvas to add a comment.</p>
            </div>
          )}

          {activeIndicators.length === 0 && completedIndicators.length > 0 && (
            <div className="text-center text-muted-foreground py-4">
              <p className="text-sm">All comments on this page have been resolved or skipped.</p>
            </div>
          )}
        </div>
      </Card>

      <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Delete comment?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              className="m-0 w-full sm:w-auto"
              data-testid="cancel-delete-comment"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteComment}
              className="m-0 w-full sm:w-auto text-white hover:opacity-90"
              style={{ backgroundColor: documentColor }}
              data-testid="confirm-delete-comment"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}